/**
 * Reports — Phase 2b implementation.
 *
 * Sections (§4 spec):
 *   - Ledger list of monthly reports:
 *       • Month name in display font (from period_start)
 *       • "{total_activity} activities" in mono
 *       • "Download PDF" button → GET /api/console/reports/:id/pdf (target=_blank)
 *   - Empty state: "Your first monthly report will appear here."
 *   - Skeleton while loading.
 *
 * Data: GET /api/console/reports  — query key ['reports']
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch, apiDownload } from '../lib/api.js';
import Skeleton from '../components/Skeleton.jsx';
import { FileBarChart2, Download, AlertCircle } from 'lucide-react';

// ── Helper ────────────────────────────────────────────────────────────────────

/**
 * Formats a YYYY-MM-DD period_start as "June 2026".
 * Reads as UTC date to avoid TZ offset shifting the month.
 */
function formatPeriod(periodStart) {
  if (!periodStart) return '';
  const d = new Date(`${periodStart}T00:00:00Z`);
  return d.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

// ── Report row ────────────────────────────────────────────────────────────────

function ReportCard({ report }) {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const monthLabel = formatPeriod(report.period_start);
  const activityLabel =
    report.total_activity != null
      ? `${report.total_activity} activities`
      : 'No activity recorded';

  async function handleDownload() {
    if (downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      await apiDownload(
        `/api/console/reports/${report.id}/pdf`,
        `titan-report-${report.period_start ?? report.id}.pdf`,
      );
    } catch (err) {
      setDownloadError(err?.message || 'Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="lrow report-row" style={{ justifyContent: 'space-between' }}>
      {/* Left: icon + text */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
        {/* Month icon badge */}
        <div
          aria-hidden="true"
          style={{
            width: '40px',
            height: '40px',
            border: '1px solid var(--line)',
            background: 'var(--acc-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <FileBarChart2
            size={18}
            color="var(--acc)"
            strokeWidth={1.75}
            aria-hidden="true"
          />
        </div>

        <div style={{ minWidth: 0 }}>
          {/* Month name */}
          <p
            className="d-xl"
            style={{
              margin: '0 0 3px',
              fontSize: '20px',
              color: 'var(--ink)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {monthLabel}
          </p>
          {/* Activity count */}
          <p
            className="mono"
            style={{
              margin: 0,
              fontSize: '12px',
              color: 'var(--ink-soft)',
              lineHeight: 1.3,
            }}
          >
            {activityLabel}
          </p>
          {/* Inline download error */}
          {downloadError && (
            <p
              role="alert"
              style={{
                margin: '4px 0 0',
                fontSize: '11px',
                color: 'var(--bad)',
                lineHeight: 1.4,
              }}
            >
              {downloadError}
            </p>
          )}
        </div>
      </div>

      {/* Right: Download PDF button — <button> so auth header is sent */}
      <button
        type="button"
        onClick={handleDownload}
        disabled={downloading}
        aria-label={`Download PDF for ${monthLabel}`}
        className="btnx primary"
        style={{ height: '40px', fontSize: '11px', padding: '0 16px', flexShrink: 0 }}
      >
        <Download size={13} strokeWidth={2.5} aria-hidden="true" />
        {downloading ? 'Downloading…' : 'Download PDF'}
      </button>
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function ReportsSkeleton() {
  return (
    <div
      style={{
        padding: '20px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
      }}
    >
      {/* Header */}
      <div>
        <Skeleton style={{ height: 30, width: '40%', marginBottom: '8px' }} />
        <Skeleton style={{ height: 16, width: '65%' }} />
      </div>
      {/* Row list */}
      <div className="panel" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <i className="brk brk-tl" /><i className="brk brk-br" />
        <Skeleton style={{ height: 11, width: '20%', marginBottom: '4px' }} />
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} style={{ height: 72 }} />
        ))}
      </div>
    </div>
  );
}

// ── Error ─────────────────────────────────────────────────────────────────────

function ReportsError({ error, refetch }) {
  return (
    <div
      style={{
        padding: '20px 16px',
        paddingTop: '64px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: '16px',
      }}
    >
      <div
        style={{
          width: '52px',
          height: '52px',
          borderRadius: '50%',
          background: 'var(--panel-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <AlertCircle size={24} color="var(--ink-soft)" strokeWidth={1.5} aria-hidden="true" />
      </div>
      <div>
        <p className="d-xl" style={{ margin: '0 0 6px', fontSize: '22px', color: 'var(--ink)' }}>
          Couldn't load reports
        </p>
        <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: '14px', lineHeight: 1.5 }}>
          {error?.message || 'Something went wrong. Tap below to try again.'}
        </p>
      </div>
      <button type="button" onClick={() => refetch()} className="btnx primary">
        Try again
      </button>
    </div>
  );
}

// ── Main screen ───────────────────────────────────────────────────────────────

export default function Reports() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reports'],
    queryFn: () => apiFetch('/api/console/reports'),
  });

  if (isLoading) return <ReportsSkeleton />;
  if (error) return <ReportsError error={error} refetch={refetch} />;

  const reports = data?.reports ?? [];

  return (
    <div
      style={{
        padding: '20px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
      }}
    >
      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div>
        <h1 className="d-xl" style={{ fontSize: '30px', color: 'var(--ink)', margin: '0 0 4px' }}>
          Reports
        </h1>
        <p className="serif-i" style={{ margin: 0, color: 'var(--ink-soft)', fontSize: '16px', lineHeight: 1.55 }}>
          Your monthly activity summaries.
        </p>
      </div>

      {/* ── Report list ───────────────────────────────────────────────────── */}
      <section aria-label="Monthly reports" className="panel" style={{ padding: '20px' }}>
        <i className="brk brk-tl" /><i className="brk brk-br" />
        <h2 className="mlabel" style={{ margin: '0 0 16px' }}>
          Monthly reports
        </h2>

        {reports.length === 0 ? (
          /* Empty state */
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '32px 0 16px',
              gap: '12px',
              textAlign: 'center',
            }}
          >
            <div
              aria-hidden="true"
              style={{
                width: '52px',
                height: '52px',
                border: '1px solid var(--line)',
                background: 'var(--acc-soft)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FileBarChart2 size={24} color="var(--acc)" strokeWidth={1.5} aria-hidden="true" />
            </div>
            <p style={{ margin: 0, fontSize: '15px', color: 'var(--ink)', fontWeight: 600, lineHeight: 1.35 }}>
              Your first monthly report will appear here.
            </p>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--ink-soft)', lineHeight: 1.5 }}>
              We compile them at the end of each month.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {reports.map((report) => (
              <ReportCard key={report.id} report={report} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
